import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Switch, Textarea } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useState } from "react";
import toast from "react-hot-toast";
import { docs as documentService } from "@/services/docs/docs-service";
import { SendDocumentPayload } from "@interfaces/document";
import { Button as CopyLinkButton } from "@/components/ui/button";
import { EventCategory, EventName } from "@interfaces/events";
import { eventsService } from "@/services/analytics/events-service";

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
  versionNumber: number;
  documentPublicId: string;
  documentName: string;
}

const initialForm: SendDocumentPayload = {
  recipientEmail: '',
  message: '',
  allowDownload: false,
}

function validateForm(form: SendDocumentPayload): string | null {
  if (!form.recipientEmail) {
    return 'Please enter a recipient email address.';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(form.recipientEmail)) {
    return 'Please enter a valid email address.';
  }

  return null;
}

const SendDocumentModal = (props: Props) => {
  const { isOpen, onClose, versionNumber, documentPublicId } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCloseDialog = () => {
    setForm(initialForm);
    setCopied(false);
    setError(false);
    setShareLink('');
    onClose?.();
  };

  const handleCopyLink = async () => {
    if (!shareLink) {
      toast.error('No link to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success('Link copied to clipboard');

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      toast.error('Failed to copy link. Please try again.');
    }
  };

  const onSubmit = async () => {
    const errorMessage = validateForm(form);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    // fire send_attempted event
    eventsService.fireEvent({
      name: EventName.SEND_ATTEMPTED,
      category: EventCategory.CORE_BEHAVIOR,
      doc_public_id: documentPublicId,
      source: undefined,
      meta: {},
    });

    setLoading(true);

    const payload: SendDocumentPayload = {
      ...form,

      versionNumber,
      documentPublicId,
      documentName: props.documentName,
    }

    await documentService.send(payload).then((res) => {
      if (res.status === 'error') {
        setError(true);
        setShareLink(res.shareLink);

        toast.error('Failed to send document. Please use the direct link.');

        return;
      }

      toast.success('Sent successfully!');
      handleCloseDialog();

      // fire send_completed event
      eventsService.fireEvent({
        name: EventName.SEND_COMPLETED,
        category: EventCategory.CORE_BEHAVIOR,
        doc_public_id: documentPublicId,
        source: undefined,
        meta: {},
      });

      // fire doc_completed event
      eventsService.fireEvent({
        name: EventName.DOC_COMPLETED,
        category: EventCategory.CORE_BEHAVIOR,
        doc_public_id: documentPublicId,
        source: undefined,
        meta: {},
      });
    }).catch(() => {
      toast.error('Failed to send document. Please try again.');
    });

    setLoading(false);
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseDialog}
        size="2xl"
        placement="top-center"
        className="max-w-[35rem] rounded-[0.25rem]"
        hideCloseButton
      >
        <ModalContent>
          <ModalHeader className="flex items-center gap-1 border-b border-gray-200 px-8 font-normal text-base">
            <Icon icon="ph:telegram-logo-thin" className="w-5 h-5 me-3" />

            Send your document

            <Icon icon="material-symbols:close-rounded" className="w-5 h-5 cursor-pointer text-gray-500 ms-auto" onClick={handleCloseDialog} />
          </ModalHeader>

          {
            !error ? (
              <ModalBody className="px-8 pt-6 gap-5">
                {/* email */}
                <div>
                  <Input
                    label="Recipient's email address"
                    labelPlacement="outside"
                    placeholder="someone@example.com"
                    fullWidth
                    type="email"
                    classNames={{
                      inputWrapper: "border border-gray-200 rounded-[0.25rem]",
                    }}
                    value={form.recipientEmail}
                    onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })}
                  />
                </div>

                {/* message */}
                <div>
                  <Textarea
                    label="Optional message"
                    labelPlacement="outside"
                    placeholder="Hey, here’s the draft we discussed."
                    fullWidth
                    value={form.message}
                    classNames={{
                      inputWrapper: "border border-gray-200 rounded-[0.25rem]",
                    }}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </div>

                {/* allow download */}
                <div className="flex items-center justify-between w-full">
                  <span className="text-sm">Allow download</span>
                  <Switch
                    isSelected={form.allowDownload}
                    onValueChange={(checked) => setForm({ ...form, allowDownload: checked })}
                    classNames={{
                      thumb: "bg-white",
                      wrapper: form.allowDownload ? "!bg-[#c6f648] !border !border-[#99cc15]" : "",
                    }}
                  />
                </div>
              </ModalBody>
            ) : (
              <ModalBody className="px-8 pt-6 gap-4">
                <p className="font-[600]">
                  Couldn’t send the email
                </p>

                <p className="text-sm">
                  You can still share the document manually using the secure link below:
                </p>

                <div className="flex items-center w-full gap-2">
                  <Input
                    placeholder={shareLink}
                    fullWidth
                    isReadOnly
                    classNames={{
                      inputWrapper: "border border-gray-200 rounded-[0.25rem]",
                    }}
                  />

                  <CopyLinkButton
                    variant="accentGreen"
                    onClick={handleCopyLink}
                  >
                    {copied ? 'Copied!' : 'Copy link'}
                  </CopyLinkButton>
                </div>
              </ModalBody>
            )
          }


          <ModalFooter className="px-8 pb-8">
            {
              !error && (
                <Button
                  isLoading={loading}
                  isDisabled={!form.recipientEmail}
                  color="primary"
                  variant="flat"
                  className="border border-[#99cc15] bg-[#c6f648] text-black rounded-[0.25rem]"
                  onPress={onSubmit}>
                  Send
                </Button>
              )
            }
          </ModalFooter>

        </ModalContent>
      </Modal>
    </>

  )
}

export default SendDocumentModal;